// src/contexts/OrganizerContext.tsx

import React, {
	createContext,
	useState,
	useEffect,
	useCallback,
	useContext,
	useRef,
} from "react";
import { useAuth } from "./AuthContext";
import { apiClient } from "../utils/api";

export interface OrganizerSocialLink {
	platform: string;
	url: string | null;
	input_value?: string | null;
	handle?: string | null;
	input_type?: string | null;
}

export interface OrganizerSocialLinkInputPayload {
	platform: string;
	value: string;
}

/** 主催者情報の型 */
interface Organizer {
	id: number;
	user_id: number;
	organizer_type: string;
	organizer_name: string;
	representative_name: string;
	email: string;
	phone: string;
	website: string;
	description: string;
	logo_url: string | null;
	is_verified: boolean;
	bank_name: string;
	branch_name: string;
	account_type: string;
	account_number: string;
	account_holder_name: string;
	created_at: string;
	updated_at: string;
	social_links?: OrganizerSocialLink[];
}

interface UpdateOrganizerProfilePayload {
	organizerName?: string;
	representativeName?: string;
	email?: string;
	phone?: string;
	website?: string;
	description?: string;
	bankName?: string;
	branchName?: string;
	accountType?: string;
	accountNumber?: string;
	accountHolderName?: string;
	socialLinks?: OrganizerSocialLinkInputPayload[];
}

/** OrganizerContext が持つ状態と操作 */
interface OrganizerContextType {
	organizer: Organizer | null;
	isOrganizer: boolean;
	isOrganizerLoading: boolean;
	hasFetched: boolean;
	fetchOrganizerData: () => Promise<void>;
	refreshOrganizerData: () => Promise<void>;
	updateOrganizerProfile: (
		data: UpdateOrganizerProfilePayload
	) => Promise<boolean>;
}

/** Context の初期値 */
export const OrganizerContext = createContext<OrganizerContextType>({
	organizer: null,
	isOrganizer: false,
	isOrganizerLoading: true,
	hasFetched: false,
	fetchOrganizerData: async () => {},
	refreshOrganizerData: async () => {},
	updateOrganizerProfile: async () => false,
});

interface OrganizerProviderProps {
	children: React.ReactNode;
}

/**
 * OrganizerProvider:
 *  - 主催者情報の管理
 *  - 主催者関連の API 呼び出し
 */
export const OrganizerProvider: React.FC<OrganizerProviderProps> = ({
	children,
}) => {
	const {
		isAuthenticated,
		isAuthLoading,
		hasFetched: authHasFetched,
	} = useAuth();
	const [organizer, setOrganizer] = useState<Organizer | null>(null);
	const [isOrganizer, setIsOrganizer] = useState(false); // 明示的なisOrganizer状態
	const [isOrganizerLoading, setIsOrganizerLoading] = useState(true);
	const [hasFetched, setHasFetched] = useState(false);

	// API呼び出しの重複防止用ref
	const fetchInProgress = useRef(false);
	// 最後のfetch完了時間
	const lastFetchTime = useRef<number | null>(null);
	// データの鮮度(ms)
	const DATA_FRESHNESS_THRESHOLD = 5000; // 5秒

	/**
	 * 主催者情報を取得する
	 * バックエンドの仕様に合わせて /organizer/check エンドポイントを使用
	 */
	const fetchOrganizerData = useCallback(async () => {
		if (isAuthLoading || !authHasFetched) {
			return;
		}

		if (!isAuthenticated) {
			setIsOrganizerLoading(false);
			setHasFetched(true);
			setIsOrganizer(false);
			return;
		}

		if (fetchInProgress.current) {
			return;
		}

		const now = Date.now();
		if (
			lastFetchTime.current &&
			now - lastFetchTime.current < DATA_FRESHNESS_THRESHOLD &&
			hasFetched
		) {
			return;
		}

		fetchInProgress.current = true;
		setIsOrganizerLoading(true);

		try {
			const response = await apiClient.get("/organizer/check");

			lastFetchTime.current = Date.now();

			if (response.data.isOrganizer === true) {
				setIsOrganizer(true);

				if (response.data.organizer) {
					setOrganizer(response.data.organizer);
				} else {
					setOrganizer(null);
				}
			} else {
				setIsOrganizer(false);
				setOrganizer(null);
			}
		} catch (_error) {
			setOrganizer(null);
			setIsOrganizer(false);
		} finally {
			setIsOrganizerLoading(false);
			setHasFetched(true);
			fetchInProgress.current = false;
		}
	}, [isAuthenticated, isAuthLoading, authHasFetched, hasFetched]);

	// refreshOrganizerData は fetchOrganizerData と同じだが、鮮度チェックを無視して強制的に再取得する
	const refreshOrganizerData = useCallback(async () => {
		if (isAuthLoading || !authHasFetched) {
			return;
		}

		if (!isAuthenticated) {
			setIsOrganizerLoading(false);
			setHasFetched(true);
			setIsOrganizer(false);
			return;
		}

		if (fetchInProgress.current) {
			return;
		}

		fetchInProgress.current = true;
		setIsOrganizerLoading(true);

		try {
			const response = await apiClient.get("/organizer/check");

			lastFetchTime.current = Date.now();

			if (response.data.isOrganizer === true) {
				setIsOrganizer(true);

				if (response.data.organizer) {
					setOrganizer(response.data.organizer);
				} else {
					setOrganizer(null);
				}
			} else {
				setIsOrganizer(false);
				setOrganizer(null);
			}
		} catch (_error) {
			setOrganizer(null);
			setIsOrganizer(false);
		} finally {
			setIsOrganizerLoading(false);
			setHasFetched(true);
			fetchInProgress.current = false;
		}
	}, [isAuthenticated, isAuthLoading, authHasFetched]);

	/**
	 * 主催者プロフィールを更新する
	 * バックエンドの仕様に合わせて /organizer/profile エンドポイントを使用
	 */
	const updateOrganizerProfile = async (
		data: UpdateOrganizerProfilePayload
	): Promise<boolean> => {
		try {
			// バックエンドのルートに合わせて /organizer/profile を使用
			const response = await apiClient.put("/organizer/profile", data);
			if (response.status === 200 && response.data.organizer) {
				setOrganizer(response.data.organizer);
				// 更新時間を記録
				lastFetchTime.current = Date.now();
				return true;
			}
			return false;
		} catch (_error) {
			return false;
		}
	};

	// 認証状態が変わったら主催者情報を取得
	useEffect(() => {
		if (authHasFetched && !isAuthLoading && isAuthenticated) {
			fetchOrganizerData();
		}
	}, [fetchOrganizerData, isAuthenticated, isAuthLoading, authHasFetched]);

	return (
		<OrganizerContext.Provider
			value={{
				organizer,
				isOrganizer,
				isOrganizerLoading,
				hasFetched,
				fetchOrganizerData,
				refreshOrganizerData,
				updateOrganizerProfile,
			}}
		>
			{children}
		</OrganizerContext.Provider>
	);
};

/** OrganizerContext を使うヘルパーフック */
export const useOrganizer = () => {
	const context = useContext(OrganizerContext);
	if (context === undefined) {
		throw new Error("useOrganizer must be used within an OrganizerProvider");
	}
	return context;
};
